import { Message } from '@/types/messages';
import { UserProfile } from '@/types/user';
import { MessageError } from '@/types/messages';
import { MessageBubble } from '@/components/messages/MessageBubble';
import { MessageInput } from './MessageInput';
import { useInView } from 'react-intersection-observer';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

export interface ConversationViewProps {
  messages: Message[];
  currentUser: UserProfile;
  onSendMessage: (content: string) => Promise<void>;
  isLoading: boolean;
  onLoadMore: () => Promise<void>;
  conversationId: string;
  messageError?: MessageError;
  onClearError?: () => void;
  hasMore?: boolean;
  otherUser?: {
    id: string;
    name: string;
    imageUrl?: string;
    userType?: 'talent' | 'promoter';
  };
}

export function ConversationView({
  messages,
  currentUser,
  onSendMessage,
  isLoading,
  onLoadMore,
  conversationId,
  messageError,
  onClearError,
  hasMore = false,
  otherUser
}: ConversationViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [prevScrollHeight, setPrevScrollHeight] = useState(0);

  const { ref: loadMoreTriggerRef } = useInView({
    onChange: (inView) => {
      if (inView && hasMore && !isLoading) {
        handleLoadMore();
      }
    },
    threshold: 0,
  });

  const handleLoadMore = async () => {
    if (containerRef.current) {
      setPrevScrollHeight(containerRef.current.scrollHeight);
    }
    await onLoadMore();
  };

  // Maintain scroll position when loading more messages
  useEffect(() => {
    if (containerRef.current && prevScrollHeight > 0) {
      const newScrollHeight = containerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeight;
      containerRef.current.scrollTop = scrollDiff;
      setPrevScrollHeight(0);
    }
  }, [messages.length, prevScrollHeight]);

  // Auto-scroll to bottom for new messages
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      const { scrollHeight, clientHeight } = containerRef.current;
      containerRef.current.scrollTop = scrollHeight - clientHeight;
    }
  }, [messages.length, autoScroll]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setAutoScroll(isNearBottom);
  };

  const getProfilePath = () => {
    if (!otherUser?.id) return '#';
    // Default to talent if userType is not specified
    const type = otherUser.userType || 'talent';
    return `/${type}/profile/${otherUser.id}`;
  };

  const isOwnMessage = (message: Message) => {
    return message.type === 'text' && message.senderId === currentUser.id;
  };

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-gray-900">
      {/* Header */}
      <div className="flex-none border-b border-gray-800 px-4 py-3 flex items-center bg-gray-900">
        {otherUser?.imageUrl && (
          <Link 
            to={getProfilePath()}
            className="hover:opacity-80 transition-opacity"
          >
            <img
              src={otherUser.imageUrl}
              alt={otherUser.name}
              className="w-8 h-8 rounded-full mr-3 cursor-pointer"
            />
          </Link>
        )}
        <div className="font-medium text-gray-100 truncate">{otherUser?.name}</div>
      </div>

      {/* Messages Container */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto p-2 sm:p-4 flex flex-col"
        onScroll={handleScroll}
      >
        {hasMore && (
          <div ref={loadMoreTriggerRef} className="h-16 flex items-center justify-center">
            {isLoading && (
              <div className="w-6 h-6 border-2 border-blue-500 rounded-full animate-spin border-t-transparent" />
            )}
          </div>
        )}
        
        {messages.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-center">
            <div>
              <p className="font-medium mb-2 text-gray-100">Start your conversation with {otherUser?.name}</p>
              <p className="text-sm text-gray-300">Send a message below to begin chatting</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col space-y-2">
            {messages.map(message => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwnMessage={isOwnMessage(message)}
                conversationId={conversationId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Message Input */}
      <div className="flex-none p-2 sm:p-4 bg-gray-900 border-t border-gray-800">
        <MessageInput
          onSendMessage={onSendMessage}
          conversationId={conversationId}
          error={messageError}
          onClearError={onClearError}
        />
      </div>
    </div>
  );
}